import { Outlet, Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/authStore';

/**
 * AuthLayout - Layout for authentication pages (login, register, etc.)
 * 
 * Features:
 * - Centered card layout
 * - Logo header
 * - Redirects authenticated users to their portal
 */
export default function AuthLayout() {
  const { isAuthenticated, user } = useAuthStore();

  // Redirect authenticated users to their portal
  if (isAuthenticated && user) {
    const redirectPath = getPortalPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Header */}
      <header className="py-6 px-8">
        <Link to="/" className="inline-flex items-center gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-sage to-sage-dark rounded-xl flex items-center justify-center text-white">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z"/>
              <path d="M12 12L12 21"/>
              <path d="M12 12L20 7.5"/>
              <path d="M12 12L4 7.5"/>
            </svg>
          </div>
          <span className="font-display text-2xl font-semibold text-navy">
            Bright<span className="text-sage">Path</span>
          </span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-8 text-center text-sm text-warm-gray">
        <p>© {new Date().getFullYear()} BrightPath. All rights reserved.</p>
      </footer>
    </div>
  );
}

/**
 * Get the portal path based on user role
 */
function getPortalPath(role: string): string {
  switch (role) {
    case 'attorney':
      return '/attorney/dashboard';
    case 'vendor':
      return '/vendor/dashboard';
    case 'admin':
      return '/admin/dashboard';
    case 'family':
    default:
      return '/family/dashboard';
  }
}

